import React, { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BadgeIcon from '@mui/icons-material/Badge';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';

interface ConfigItem {
  label: string;
  path: string;
}

interface ConfigCardProps {
  title: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  items: ConfigItem[];
}

const ConfigCard: FC<ConfigCardProps> = ({ title, icon, color, bgColor, items }) => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        width: 260,
        minHeight: 280,
        background: '#fff',
        border: '2px solid #d1d5db',
        borderRadius: '16px',
        padding: '10px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          padding: '12px 14px',
          background: bgColor,
          borderRadius: '8px',
          mt: -0.5,
          mb: 1,
        }}
      >
        <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111' }}>{title}</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {items.map((item) => (
          <Box
            key={item.label}
            onClick={() => navigate(item.path)}
            sx={{
              padding: '5px 10px',
              fontSize: 13,
              color: '#374151',
              cursor: 'pointer',
              borderRadius: '6px',
              transition: 'background 0.15s ease',
              '&:hover': { background: '#f3f4f6', color },
            }}
          >
            {item.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: FC<SectionProps> = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#111', mb: 1 }}>{title}</Typography>
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>{children}</Box>
  </Box>
);

const Config: FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  // Reset scroll to the top on mount.
  useEffect(() => {
    window.scrollTo(0, 0);
    // Some layouts scroll an inner container rather than the window.
    document.querySelector('main')?.scrollTo?.(0, 0);
  }, []);

  return (
    <Box sx={{ background: '#f9fafb' }}>
      <Box sx={{ padding: '12px 24px', maxWidth: 1400, margin: '0 auto' }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'center' }}>
        <Section title="People Settings">
          <ConfigCard
            title="Employee"
            icon={<BadgeIcon sx={{ fontSize: 20 }} />}
            color="#3b82f6"
            bgColor="#dbeafe"
            items={[
              { label: 'Employees', path: '/people/employee' },
              { label: 'Employee Invite', path: '/people/employee' },
              { label: 'Roles', path: '/users' },
            ]}
          />
          <ConfigCard
            title="People"
            icon={<PeopleAltIcon sx={{ fontSize: 20 }} />}
            color="#10b981"
            bgColor="#d1fae5"
            items={[
              { label: 'Leave Config', path: '/people/employee-config?tab=0' },
              { label: 'Shifts', path: '/people/employee-config?tab=1' },
              { label: 'Holidays', path: '/people/employee-config?tab=2' },
              { label: 'Week Offs', path: '/people/employee-config?tab=3' },
              { label: 'Shift Pattern Config', path: '/people/employee-config?tab=4' },
              { label: 'Employment Assignment', path: '/people/employee-config?tab=5' },
              { label: 'Work Locations', path: '/people/employee-config?tab=6' },
            ]}
          />
        </Section>

        <Section title="Payroll Settings">
          <ConfigCard
            title="Payroll"
            icon={<AccountBalanceIcon sx={{ fontSize: 20 }} />}
            color="#f59e0b"
            bgColor="#fef3c7"
            items={[
              { label: 'Salary Heads', path: '/people/payroll-settings?tab=salaryHead' },
              { label: 'Salary Grades', path: '/people/payroll-settings?tab=salaryGrade' },
              { label: 'Employee Salary Config', path: '/people/payroll-settings?tab=employeeSalaryConfig' },
              { label: 'Loans', path: '/people/payroll-settings?tab=loan' },
            ]}
          />
          <ConfigCard
            title="Salary Management"
            icon={<MonetizationOnIcon sx={{ fontSize: 20 }} />}
            color="#8b5cf6"
            bgColor="#ede9fe"
            items={[
              { label: 'Salary Management', path: '/people/salary/list' },
              { label: 'Process Salary', path: '/people/salary/list?open=process-salary' },
              { label: 'Statutory Compliance', path: '/coa/statutory-compliance' },
              { label: 'Expenses', path: '/expenses' },
            ]}
          />
        </Section>
        </Box>
      </Box>
    </Box>
  );
};

export default Config;
